import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, ChevronDown, Mail } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

interface NavItem {
  label: string;
  href: string;
  children?: NavItem[];
}

const navigation: NavItem[] = [
  {
    label: "Startseite",
    href: "/",
    children: [
      { label: "Zur Person", href: "/zur-person/" },
      { label: "Präsenz & Online", href: "/mensch-im-fokus/praesenz-und-online/" },
    ],
  },
  {
    label: "Berufliche Themen",
    href: "/consulting/",
    children: [
      { label: "Team- & Fallsupervision", href: "/consulting/supervision/" },
      { label: "Coaching & Einzelsupervision", href: "/consulting/coaching/" },
      { label: "Strategieentwicklung", href: "/consulting/strategieentwicklung/" },
      { label: "Lehrsupervision", href: "/consulting/lehrsupervision/" },
      { label: "Wissen", href: "/consulting/wissen/" },
    ],
  },
  {
    label: "Persönliche Themen",
    href: "/mensch-im-fokus/",
    children: [
      {
        label: "Gestaltpsychotherapie",
        href: "/mensch-im-fokus/gestaltpsychotherapie/",
        children: [
          { label: "Wissen", href: "/mensch-im-fokus/gestaltpsychotherapie/wissen/" },
        ],
      },
      {
        label: "Beratung für Paare & Familien",
        href: "/mensch-im-fokus/beratung-paare-familien/",
        children: [
          { label: "Wissen", href: "/mensch-im-fokus/beratung-paare-familien/wissen/" },
        ],
      },
      {
        label: "Yoga",
        href: "/mensch-im-fokus/yoga/",
        children: [
          { label: "Einzelunterricht", href: "/mensch-im-fokus/yoga/einzelunterricht/" },
          { label: "Gruppenangebote", href: "/mensch-im-fokus/yoga/gruppen/" },
          { label: "Onko-Yoga", href: "/mensch-im-fokus/yoga/onko-yoga/" },
          { label: "Seminar Sylt", href: "/mensch-im-fokus/yoga/seminare/" },
          { label: "Supervision", href: "/mensch-im-fokus/yoga/supervision/" },
          { label: "Fortbildung", href: "/mensch-im-fokus/yoga/fortbildung/" },
          { label: "Wissen", href: "/mensch-im-fokus/yoga/wissen/" },
        ],
      },
    ],
  },
];

// =====================================================
// Desktop Top-Bar
// =====================================================

const DesktopDropdownLink = ({
  href,
  label,
  active,
  indent = 0,
}: {
  href: string;
  label: string;
  active: boolean;
  indent?: number;
}) => (
  <NavigationMenuLink asChild>
    <Link
      to={href}
      className={cn(
        "block rounded-md px-3 py-2 text-sm transition-colors hover:bg-muted",
        indent > 0 && "pl-6 text-muted-foreground",
        active && "font-medium text-foreground",
      )}
    >
      {label}
    </Link>
  </NavigationMenuLink>
);

const DesktopDropdownSection = ({
  item,
  currentPath,
  accent,
  width = "w-64",
}: {
  item: NavItem;
  currentPath: string;
  accent: "consulting" | "mif";
  width?: string;
}) => {
  const isActive = currentPath === item.href;
  const isInSection = currentPath.startsWith(item.href);

  return (
    <NavigationMenuItem>
      <NavigationMenuTrigger
        className={cn(
          "bg-transparent text-sm font-medium text-foreground border border-transparent data-[active]:bg-transparent data-[state=open]:bg-transparent hover:bg-transparent data-[active]:text-foreground data-[state=open]:text-foreground hover:text-foreground focus:bg-transparent focus:text-foreground",
          isInSection &&
            (accent === "consulting"
              ? "border-consulting/30"
              : "border-mif/30"),
        )}
      >
        {item.label}
      </NavigationMenuTrigger>
      <NavigationMenuContent>
        <div className={cn("p-2 space-y-0.5", width)}>
          <DesktopDropdownLink
            href={item.href}
            label="Übersicht"
            active={isActive}
          />
          {item.children?.map((child) => {
            const childActive = currentPath === child.href;
            return (
              <div key={child.href}>
                <DesktopDropdownLink
                  href={child.href}
                  label={child.label}
                  active={childActive}
                />
                {child.children?.map((gc) => (
                  <DesktopDropdownLink
                    key={gc.href}
                    href={gc.href}
                    label={gc.label}
                    active={currentPath === gc.href}
                    indent={1}
                  />
                ))}
              </div>
            );
          })}
        </div>
      </NavigationMenuContent>
    </NavigationMenuItem>
  );
};

const MifMegaMenu = ({
  item,
  currentPath,
}: {
  item: NavItem;
  currentPath: string;
}) => {
  const isInSection = currentPath.startsWith(item.href);

  return (
    <NavigationMenuItem>
      <NavigationMenuTrigger
        className={cn(
          "bg-transparent text-sm font-medium text-foreground border border-transparent data-[active]:bg-transparent data-[state=open]:bg-transparent hover:bg-transparent data-[active]:text-foreground data-[state=open]:text-foreground hover:text-foreground focus:bg-transparent focus:text-foreground",
          isInSection && "border-mif/30",
        )}
      >
        {item.label}
      </NavigationMenuTrigger>
      <NavigationMenuContent>
        <div className="p-4 grid grid-cols-3 gap-6 w-[640px]">
          {item.children?.map((column) => {
            const colActive = currentPath === column.href;
            return (
              <div key={column.href} className="space-y-1">
                <div className="pb-2 mb-1 border-b border-mif/30">
                  <NavigationMenuLink asChild>
                    <Link
                      to={column.href}
                      className={cn(
                        "block text-sm font-semibold text-mif hover:opacity-80 transition-opacity",
                      )}
                    >
                      {column.label}
                    </Link>
                  </NavigationMenuLink>
                </div>
                <NavigationMenuLink asChild>
                  <Link
                    to={column.href}
                    className={cn(
                      "block rounded-md px-2 py-1.5 text-xs italic text-muted-foreground hover:bg-muted hover:text-foreground transition-colors",
                      colActive && "font-medium text-foreground",
                    )}
                  >
                    Übersicht
                  </Link>
                </NavigationMenuLink>
                {column.children?.map((leaf) => {
                  const leafActive = currentPath === leaf.href;
                  return (
                    <NavigationMenuLink key={leaf.href} asChild>
                      <Link
                        to={leaf.href}
                        className={cn(
                          "block rounded-md px-2 py-1.5 text-sm transition-colors hover:bg-muted",
                          leafActive ? "font-medium text-foreground" : "text-foreground/80",
                        )}
                      >
                        {leaf.label}
                      </Link>
                    </NavigationMenuLink>
                  );
                })}
              </div>
            );
          })}
        </div>
      </NavigationMenuContent>
    </NavigationMenuItem>
  );
};

const DesktopHeader = ({ currentPath }: { currentPath: string }) => {
  const consulting = navigation[1];
  const mif = navigation[2];
  const [openValue, setOpenValue] = useState("");
  const isOpen = openValue !== "";

  return (
    <>
      {/* Scrim behind open dropdown */}
      <div
        aria-hidden
        className={cn(
          "hidden md:block fixed inset-0 top-14 z-40 bg-foreground/30 transition-opacity duration-200 pointer-events-none",
          isOpen ? "opacity-100" : "opacity-0",
        )}
      />
      <div className="hidden md:block fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-white/95 border-b border-border/60 shadow-[0_1px_0_0_hsl(var(--border)/0.4),0_4px_12px_-8px_hsl(var(--foreground)/0.08)]">
        <div className="max-w-7xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link
            to="/"
            className="text-base font-semibold uppercase tracking-[0.1em] text-foreground hover:opacity-70 transition-opacity"
          >
            NIKOLA KNORR
          </Link>

          <NavigationMenu value={openValue} onValueChange={setOpenValue}>
            <NavigationMenuList className="gap-1">
              <NavigationMenuItem>
                <NavigationMenuLink asChild>
                  <Link
                    to="/zur-person/"
                    className={cn(
                      "inline-flex items-center px-3 py-2 text-sm font-medium rounded-md border border-transparent hover:bg-muted transition-colors",
                      currentPath === "/zur-person/" && "border-foreground/30",
                    )}
                  >
                    Zur Person
                  </Link>
                </NavigationMenuLink>
              </NavigationMenuItem>

              <DesktopDropdownSection
                item={consulting}
                currentPath={currentPath}
                accent="consulting"
              />

              <MifMegaMenu item={mif} currentPath={currentPath} />
            </NavigationMenuList>
          </NavigationMenu>

          <Button asChild variant={currentPath.startsWith("/consulting") ? "consultingPrimary" : currentPath.startsWith("/mensch-im-fokus") ? "mifPrimary" : "secondary"} className="rounded-full">
            <Link to="/kontakt/" aria-current={currentPath === "/kontakt/" ? "page" : undefined}>
              <Mail className="w-4 h-4" /> Kontakt
            </Link>
          </Button>
        </div>
      </div>
      {/* Spacer to offset fixed header for content breathing room */}
      <div aria-hidden className="hidden md:block h-20" />
    </>
  );
};


// =====================================================
// Mobile Sheet (unchanged behavior)
// =====================================================

const StartseiteNavItem = ({
  item,
  currentPath,
  onNavigate,
}: {
  item: NavItem;
  currentPath: string;
  onNavigate: () => void;
}) => {
  const isActive = currentPath === item.href;

  return (
    <div className="space-y-1">
      <Link
        to={item.href}
        onClick={onNavigate}
        className={cn(
          "block py-2 text-lg font-medium transition-colors",
          isActive ? "text-mif" : "text-muted-foreground hover:text-foreground",
        )}
      >
        {item.label}
      </Link>
      {item.children && (
        <div className="pl-4 space-y-1">
          {item.children.map((child) => (
            <Link
              key={child.href}
              to={child.href}
              onClick={onNavigate}
              className={cn(
                "block py-1.5 text-base transition-colors",
                currentPath === child.href ? "text-mif" : "text-muted-foreground hover:text-foreground",
              )}
            >
              {child.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

const MegaNavItem = ({
  item,
  currentPath,
  onNavigate,
  colorScheme,
}: {
  item: NavItem;
  currentPath: string;
  onNavigate: () => void;
  colorScheme: "consulting" | "mif";
}) => {
  const isActive = currentPath === item.href;
  const isInSection = currentPath.startsWith(item.href);
  const hasChildren = item.children && item.children.length > 0;

  const activeColor = colorScheme === "consulting" ? "text-consulting" : "text-mif";

  if (colorScheme === "mif") {
    return (
      <Collapsible defaultOpen={isInSection}>
        <CollapsibleTrigger className="w-full flex items-center py-2 text-left group">
          <span
            className={cn(
              "flex-1 text-lg font-medium transition-colors",
              isActive && activeColor,
              !isActive && isInSection && "text-foreground",
              !isActive && !isInSection && "text-muted-foreground group-hover:text-foreground",
            )}
          >
            {item.label}
          </span>
          {hasChildren && (
            <ChevronDown className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-transform duration-200 [[data-state=open]_&]:rotate-180" />
          )}
        </CollapsibleTrigger>
        {hasChildren && (
          <CollapsibleContent>
            <div className="bg-muted/50 rounded-lg p-3 mt-1 mb-2 border border-border/50 space-y-1">
              <Link
                to={item.href}
                onClick={onNavigate}
                className={cn(
                  "block py-1.5 text-sm italic transition-colors",
                  isActive ? activeColor : "text-muted-foreground hover:text-foreground",
                )}
              >
                Übersicht
              </Link>
              {item.children!.map((child) => {
                const childActive = currentPath === child.href;
                const childInSection = currentPath.startsWith(child.href);
                const hasGrandchildren = child.children && child.children.length > 0;

                return (
                  <Collapsible key={child.href} defaultOpen={childInSection}>
                    {hasGrandchildren ? (
                      <CollapsibleTrigger className="w-full flex items-center py-1.5 text-left group">
                        <span
                          className={cn(
                            "flex-1 font-medium text-sm transition-colors",
                            childActive && activeColor,
                            !childActive && childInSection && "text-foreground",
                            !childActive && !childInSection && "text-foreground group-hover:opacity-70",
                          )}
                        >
                          {child.label}
                        </span>
                        <ChevronDown className="h-3 w-3 text-muted-foreground group-hover:text-foreground transition-transform duration-200 [[data-state=open]_&]:rotate-180" />
                      </CollapsibleTrigger>
                    ) : (
                      <Link
                        to={child.href}
                        onClick={onNavigate}
                        className={cn(
                          "block py-1.5 font-medium text-sm transition-colors",
                          childActive && activeColor,
                          !childActive && childInSection && "text-foreground",
                          !childActive && !childInSection && "text-foreground hover:opacity-70",
                        )}
                      >
                        {child.label}
                      </Link>
                    )}
                    {hasGrandchildren && (
                      <CollapsibleContent>
                        <div className="pl-3 space-y-0.5 pb-1">
                          <Link
                            to={child.href}
                            onClick={onNavigate}
                            className={cn(
                              "block text-sm py-1 italic transition-colors",
                              childActive ? activeColor : "text-muted-foreground hover:text-foreground",
                            )}
                          >
                            Übersicht
                          </Link>
                          {child.children!.map((grandchild) => (
                            <Link
                              key={grandchild.href}
                              to={grandchild.href}
                              onClick={onNavigate}
                              className={cn(
                                "block text-sm py-1 transition-colors",
                                currentPath === grandchild.href ? activeColor : "text-muted-foreground hover:text-foreground",
                              )}
                            >
                              {grandchild.label}
                            </Link>
                          ))}
                        </div>
                      </CollapsibleContent>
                    )}
                  </Collapsible>
                );
              })}
            </div>
          </CollapsibleContent>
        )}
      </Collapsible>
    );
  }

  return (
    <Collapsible defaultOpen={isInSection}>
      <CollapsibleTrigger className="w-full flex items-center py-2 text-left group">
        <span
          className={cn(
            "flex-1 text-lg font-medium transition-colors",
            isActive && activeColor,
            !isActive && isInSection && "text-foreground",
            !isActive && !isInSection && "text-muted-foreground group-hover:text-foreground",
          )}
        >
          {item.label}
        </span>
        {hasChildren && (
          <ChevronDown className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-transform duration-200 [[data-state=open]_&]:rotate-180" />
        )}
      </CollapsibleTrigger>
      {hasChildren && (
        <CollapsibleContent>
          <div className="bg-muted/50 rounded-lg p-3 mt-1 mb-2 border border-border/50 space-y-0.5">
            <Link
              to={item.href}
              onClick={onNavigate}
              className={cn(
                "block py-1.5 text-sm italic transition-colors",
                isActive ? activeColor : "text-muted-foreground hover:text-foreground",
              )}
            >
              Übersicht
            </Link>
            {item.children!.map((child) => {
              const childActive = currentPath === child.href;
              return (
                <Link
                  key={child.href}
                  to={child.href}
                  onClick={onNavigate}
                  className={cn(
                    "block py-1.5 text-sm transition-colors",
                    childActive ? activeColor : "text-foreground hover:opacity-70",
                  )}
                >
                  {child.label}
                </Link>
              );
            })}
          </div>
        </CollapsibleContent>
      )}
    </Collapsible>
  );
};

const MobileHeader = ({ currentPath }: { currentPath: string }) => {
  const [isOpen, setIsOpen] = useState(false);
  const handleNavigate = () => setIsOpen(false);

  return (
    <div className="md:hidden fixed top-4 right-4 z-50">
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetTrigger asChild>
          <button
            className="w-12 h-12 bg-mif text-white rounded-full shadow-lg hover:bg-mif/90 transition-colors flex items-center justify-center"
            aria-label="Navigation öffnen"
          >
            <Menu className="w-6 h-6" />
          </button>
        </SheetTrigger>
        <SheetContent side="right" className="w-[300px] sm:w-[350px] flex flex-col pt-8">
          <nav className="space-y-2 flex-1 overflow-y-auto">
            <StartseiteNavItem
              item={navigation[0]}
              currentPath={currentPath}
              onNavigate={handleNavigate}
            />
            <MegaNavItem
              item={navigation[1]}
              currentPath={currentPath}
              onNavigate={handleNavigate}
              colorScheme="consulting"
            />
            <MegaNavItem
              item={navigation[2]}
              currentPath={currentPath}
              onNavigate={handleNavigate}
              colorScheme="mif"
            />
            <Link
              to="/kontakt/"
              onClick={handleNavigate}
              className="block py-2 text-lg font-medium text-mif hover:opacity-70 transition-opacity"
            >
              Kontakt
            </Link>
          </nav>

          <div className="pt-6 mt-6 border-t border-border">
            <div className="flex gap-4 text-sm text-muted-foreground">
              <Link to="/impressum/" onClick={handleNavigate} className="hover:text-foreground transition-colors">
                Impressum
              </Link>
              <Link to="/datenschutz/" onClick={handleNavigate} className="hover:text-foreground transition-colors">
                Datenschutz
              </Link>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

// =====================================================
// Header (combined)
// =====================================================

const Header = () => {
  const location = useLocation();
  return (
    <>
      <DesktopHeader currentPath={location.pathname} />
      <MobileHeader currentPath={location.pathname} />
    </>
  );
};

export default Header;
